import { useMediaQuery } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { FC, useMemo } from 'react';
import { Colors, muiTheme } from '../../../theme';
import { Spacing } from '../../../utils';
import { Icon, Icons, Typography } from '../../atoms';
import { ContainerV2 } from '../../atoms/containerV2/ContainerV2';
import { Checkbox } from '../checkbox/Checkbox';
import { StyledHelperText, StyledLabel, StyledMenuItem } from './InputSelect.styles';

export type MenuItemType = string | { value: string; label: string };

export type InputSelectProps = {
  value: string | string[] | null;
  onChange: (selectedValues: string | string[] | null) => void;
  label?: string;
  placeholder?: string;
  menuItems?: MenuItemType[];
  isRequired?: boolean;
  isFullWidth?: boolean;
  helperText?: string;
  helperTextColor?: Colors;
  isDisabled?: boolean;
  selectId?: string;
  allowMultipleSelection?: boolean;
  customHeight?: string | Spacing;
  renderMenuItem?: (item: string, isSelected: boolean) => React.ReactNode;
};

export const InputSelect: FC<React.PropsWithChildren<InputSelectProps>> = ({
  value,
  onChange,
  label,
  placeholder,
  menuItems = [],
  isRequired = false,
  allowMultipleSelection = false,
  isFullWidth = false,
  helperText = false,
  helperTextColor = 'textGray',
  isDisabled = false,
  selectId = 'select-id',
  customHeight = [2],
  renderMenuItem,
}) => {
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));

  const normalizedMenuItems = useMemo(() => {
    return menuItems
      .map((item) => (typeof item === 'string' ? { value: item, label: item } : item))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [menuItems]);

  const isSelected = (itemValue: string) => {
    if (allowMultipleSelection) {
      return Array.isArray(value) && value.includes(itemValue);
    }
    return value === itemValue;
  };

  const handleOptionChange = (event: SelectChangeEvent<string | string[]>) => {
    const selected = event.target.value;
    if (allowMultipleSelection) {
      onChange(typeof selected === 'string' ? selected.split(',') : selected);
    } else {
      onChange(selected);
    }
  };

  const renderFlagLabel = (itemValue: string) => {
    if (!renderMenuItem) return <Typography>{itemValue}</Typography>;
    return renderMenuItem(itemValue, isSelected(itemValue));
  };

  const renderedValue = (selected: string | string[]) => {
    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
      return <Typography color="textGray">{placeholder}</Typography>;
    }

    if (allowMultipleSelection) {
      return (
        <ContainerV2 display="flex" flexWrap="wrap">
          {Array.isArray(selected)
            ? selected.map((val) => (
                <ContainerV2 key={val} display="flex" alignItems="center" data-testid="multiple-selected-item">
                  {renderFlagLabel(val)}
                </ContainerV2>
              ))
            : null}
        </ContainerV2>
      );
    }

    return (
      <ContainerV2 display="flex" alignItems="center" height={customHeight}>
        {renderFlagLabel(selected as string)}
      </ContainerV2>
    );
  };

  return (
    <ContainerV2 width={isFullWidth ? '100%' : 'auto'}>
      {label && (
        <StyledLabel disabled={isDisabled} data-testid="input-select-label" isRequired={isRequired}>
          {label}
        </StyledLabel>
      )}
      <FormControl
        data-testid="input-form"
        disabled={isDisabled}
        fullWidth={isFullWidth}
        sx={{
          width: isFullWidth ? '100%' : isMobile ? 260 : 360,
          minWidth: isFullWidth ? '100%' : undefined,
        }}
      >
        <Select
          labelId="select-label"
          id={selectId}
          data-testid="select"
          value={value || (allowMultipleSelection ? [] : '')}
          displayEmpty
          multiple={allowMultipleSelection}
          onChange={handleOptionChange}
          renderValue={(selected) => renderedValue(selected)}
          required={isRequired}
          IconComponent={(props) => (
            <Icon data-testid="arrow" icon={Icons.DROPDOWN} size="small" margin={[0, 2]} {...props} />
          )}
        >
          {normalizedMenuItems.map((item) => (
            <StyledMenuItem key={item.label} value={item.value}>
              {renderMenuItem ? (
                renderMenuItem(item.value, isSelected(item.value))
              ) : allowMultipleSelection ? (
                <Checkbox label={item.label} isChecked={isSelected(item.value)} onChange={() => {}} />
              ) : (
                <Typography data-testid="single-selected-item">{item.label}</Typography>
              )}
            </StyledMenuItem>
          ))}
        </Select>
        {helperText && (
          <StyledHelperText
            type="caption"
            margin={[1, 0, 0]}
            $helperTextColor={helperTextColor}
            data-testid="helper-text"
          >
            {helperText}
          </StyledHelperText>
        )}
      </FormControl>
    </ContainerV2>
  );
};
